import { useState, useRef } from "react";
import { usePageTitle } from "@/hooks/use-page-title";
import { useMutation } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Wallet,
  Building2,
  Zap,
  CheckCircle2,
  ArrowRight,
  LogIn,
  ClipboardList,
  CreditCard,
} from "lucide-react";

const formSchema = z.object({
  organizationName: z.string().min(1, "Organization name is required"),
  contactName: z.string().min(1, "Contact name is required"),
  email: z.string().email("Please enter a valid email address"),
  phone: z.string().min(1, "Phone number is required"),
  organizationType: z.string().min(1, "Please select an organization type"),
  numberOfLocations: z.string().min(1, "Please select number of locations"),
  ein: z.string().min(1, "EIN / Tax ID is required"),
  message: z.string().max(500, "Please keep your message under 500 characters").optional(),
});

type FormValues = z.infer<typeof formSchema>;

const steps = [
  {
    icon: Wallet,
    step: "1",
    title: "Patient pre-funds their HealthWallet",
    description:
      "Before a scheduled visit, patients deposit funds into their HealthWallet — alone, through family contributions, or via employer matching.",
  },
  {
    icon: ClipboardList,
    step: "2",
    title: "Provider submits a payment request",
    description:
      "At checkout, your billing team submits a payment request through the HealthWallet partner portal. No claims forms, no coding delays.",
  },
  {
    icon: CreditCard,
    step: "3",
    title: "Funds transfer instantly via Stripe",
    description:
      "Pre-funded amounts are released to your account the same day. No collections process, no write-offs, no waiting on insurance.",
  },
];

export default function HospitalPartnersPublicPage() {
  usePageTitle(
    "Partner with HealthWallet",
    "Join health systems receiving guaranteed, same-day payments from pre-funded patient wallets. Apply to become a HealthWallet partner."
  );

  const formRef = useRef<HTMLDivElement>(null);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const form = useForm<FormValues>({
    mode: 'onBlur',
    resolver: zodResolver(formSchema),
    defaultValues: {
      organizationName: "",
      contactName: "",
      email: "",
      phone: "",
      organizationType: "",
      numberOfLocations: "",
      ein: "",
      message: "",
    },
  });

  const mutation = useMutation({
    mutationFn: async (data: FormValues) => {
      await apiRequest("POST", "/api/hospital-partner-applications", data);
    },
    onSuccess: () => {
      setSubmitted(true);
      setSubmitError(null);
    },
    onError: (error: Error) => {
      setSubmitError(error.message || "Something went wrong. Please try again.");
    },
  });

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const messageValue = form.watch("message") ?? "";

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* ── Hero ── */}
      <section className="relative overflow-hidden bg-gradient-to-br from-teal-900 via-teal-800 to-teal-700">
        <div className="absolute inset-0 bg-black/40" />
        <div className="relative z-10 mx-auto max-w-5xl px-4 py-20 sm:px-6 sm:py-28 text-white">
          <div className="flex items-center gap-2 mb-4">
            <Building2 className="w-5 h-5 text-teal-300" />
            <span className="text-sm font-medium text-teal-300 uppercase tracking-wider">
              For Health Systems &amp; Clinics
            </span>
          </div>
          <h1 className="text-3xl sm:text-5xl font-bold leading-tight mb-4">
            Partner with HealthWallet —{" "}
            <span className="text-teal-300">Get Paid Instantly</span>
          </h1>
          <p className="text-lg sm:text-xl text-white/80 max-w-2xl mb-8 leading-relaxed">
            Join 2 health systems already receiving guaranteed, same-day payments
            from pre-funded patient wallets. No collections. No write-offs.
          </p>
          <div className="flex flex-wrap gap-3">
            <Button
              size="lg"
              onClick={scrollToForm}
              className="bg-teal-500 border-teal-500 text-white"
              data-testid="button-apply-to-partner"
            >
              Apply to Partner
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <Link href="/auth">
              <Button
                size="lg"
                variant="outline"
                className="bg-white/10 border-white/40 text-white backdrop-blur-sm"
                data-testid="button-partner-sign-in"
              >
                <LogIn className="w-4 h-4 mr-2" />
                Partner Sign In
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* ── How It Works ── */}
      <section className="mx-auto max-w-5xl px-4 py-16 sm:px-6 sm:py-20">
        <div className="text-center mb-12">
          <h2 className="text-2xl sm:text-3xl font-bold mb-3">How It Works</h2>
          <p className="text-muted-foreground max-w-xl mx-auto">
            Three simple steps from patient wallet to provider payment — with no
            intermediary delays.
          </p>
        </div>
        <div className="grid gap-6 sm:grid-cols-3">
          {steps.map(({ icon: Icon, step, title, description }) => (
            <Card key={step} className="relative">
              <CardContent className="pt-6 pb-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="flex items-center justify-center w-9 h-9 rounded-full bg-teal-100 dark:bg-teal-900/40 text-teal-700 dark:text-teal-300 font-bold text-sm shrink-0">
                    {step}
                  </div>
                  <Icon className="w-5 h-5 text-teal-600 dark:text-teal-400" />
                </div>
                <h3 className="font-semibold text-base mb-2">{title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{description}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* ── Application Form ── */}
      <section
        ref={formRef}
        className="bg-muted/40 border-t border-border"
      >
        <div className="mx-auto max-w-2xl px-4 py-16 sm:px-6 sm:py-20">
          {submitted ? (
            <div
              className="flex flex-col items-center text-center gap-4 py-12"
              data-testid="status-application-success"
            >
              <CheckCircle2 className="w-14 h-14 text-teal-600" />
              <h2 className="text-2xl font-bold">Application Received</h2>
              <p className="text-muted-foreground max-w-sm">
                Our partnerships team will contact you within 2 business days.
              </p>
              <Link href="/">
                <Button variant="outline" className="mt-2">
                  Return to Home
                </Button>
              </Link>
            </div>
          ) : (
            <>
              <div className="mb-8">
                <h2 className="text-2xl sm:text-3xl font-bold mb-2">
                  Submit Partnership Application
                </h2>
                <p className="text-muted-foreground">
                  Tell us about your organization and we'll be in touch within 2 business days.
                </p>
              </div>

              {submitError && (
                <div
                  className="mb-6 rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive"
                  data-testid="status-submit-error"
                >
                  {submitError}
                </div>
              )}

              <Form {...form}>
                <form
                  onSubmit={form.handleSubmit((data) => mutation.mutate(data))}
                  className="space-y-5"
                >
                  <div className="grid gap-5 sm:grid-cols-2">
                    <FormField
                      control={form.control}
                      name="organizationName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Organization Name</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="City Medical Center"
                              data-testid="input-organization-name"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="contactName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Contact Name</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="Jane Smith"
                              data-testid="input-contact-name"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="grid gap-5 sm:grid-cols-2">
                    <FormField
                      control={form.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Email</FormLabel>
                          <FormControl>
                            <Input
                              type="email"
                              placeholder="jane@citymedical.org"
                              data-testid="input-email"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="phone"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Phone</FormLabel>
                          <FormControl>
                            <Input
                              type="tel"
                              placeholder="(555) 000-0000"
                              data-testid="input-phone"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="grid gap-5 sm:grid-cols-2">
                    <FormField
                      control={form.control}
                      name="organizationType"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Organization Type</FormLabel>
                          <Select onValueChange={field.onChange} value={field.value}>
                            <FormControl>
                              <SelectTrigger data-testid="select-organization-type">
                                <SelectValue placeholder="Select type" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="hospital_system">Hospital System</SelectItem>
                              <SelectItem value="clinic">Clinic</SelectItem>
                              <SelectItem value="specialty_practice">Specialty Practice</SelectItem>
                              <SelectItem value="urgent_care">Urgent Care</SelectItem>
                              <SelectItem value="community_health_center">Community Health Center</SelectItem>
                              <SelectItem value="other">Other</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="numberOfLocations"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Number of Locations</FormLabel>
                          <Select onValueChange={field.onChange} value={field.value}>
                            <FormControl>
                              <SelectTrigger data-testid="select-number-of-locations">
                                <SelectValue placeholder="Select range" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="1">1</SelectItem>
                              <SelectItem value="2-5">2–5</SelectItem>
                              <SelectItem value="6-20">6–20</SelectItem>
                              <SelectItem value="21-50">21–50</SelectItem>
                              <SelectItem value="50+">50+</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <FormField
                    control={form.control}
                    name="ein"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>EIN / Tax ID</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="12-3456789"
                            data-testid="input-ein"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="message"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          Why do you want to partner with HealthWallet?
                          <span className="ml-2 text-xs text-muted-foreground font-normal">
                            ({messageValue.length}/500)
                          </span>
                        </FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder="Tell us about your patient payment challenges and what you hope to achieve..."
                            className="min-h-[110px] resize-none"
                            data-testid="textarea-message"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <Button
                    type="submit"
                    size="lg"
                    className="w-full"
                    disabled={mutation.isPending}
                    data-testid="button-submit-application"
                  >
                    {mutation.isPending ? "Submitting…" : "Submit Partnership Application"}
                    {!mutation.isPending && <Zap className="w-4 h-4 ml-2" />}
                  </Button>
                </form>
              </Form>
            </>
          )}
        </div>
      </section>
    </div>
  );
}
